import { __awaiter, __generator } from "tslib";
import { fetchFeatureGates, getFeatureGates, } from '@office-start/feature-gates-service';
import { fetchFeatureSettings, getFeatureSettings, } from '@office-start/feature-settings-service';
import { mark } from '@office-start/performance-service';
import { logError } from '@office-start/telemetry-service';
import { fetchUserEntitlements, getUserEntitlements, } from '@office-start/user-entitlements-service';
import { validateConfigEquality } from './helpers/validateConfigEquality';
import { logFetchConfigInternalFinished, } from './telemetry/logFetchConfigInternalFinished';
/**
 * Depending on the options passed in, this function will attempt to obtain a config object that is generated client-side or server-side.
 * If both client and server configs are obtained, they will be checked for equality. Client config will be returned if equal, else server config will be returned.
 * @param fetchDataPromisesObject Object that contains promises of all API data values needed for client-side config resolution.
 * @param options Config specific options that configure this function to obtain a configuration object.
 * @returns Configuration object which should then be stored in ReactQuery cache.
 */
export function fetchConfigInternal(fetchDataPromisesObject, options) {
    return __awaiter(this, void 0, void 0, function () {
        var serverConfig, clientConfig, result, key, telemetryParams, featureGatesResult, featureGates, _a, res, getClientGeneratedConfigEnabled, getClientGeneratedConfig, res, configsAreEqual;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    key = options.key;
                    telemetryParams = {
                        ConfigKey: key,
                        Completed: false,
                        Status: 'Undefined',
                    };
                    featureGatesResult = getFeatureGates();
                    if (!(featureGatesResult === null || featureGatesResult === void 0 ? void 0 : featureGatesResult.result)) return [3 /*break*/, 1];
                    _a = featureGatesResult;
                    return [3 /*break*/, 3];
                case 1: return [4 /*yield*/, fetchFeatureGates()];
                case 2:
                    _a = _b.sent();
                    _b.label = 3;
                case 3:
                    featureGates = _a;
                    options = tryDisableGetServerConfig(featureGates, options);
                    if (!canGetServerGeneratedConfig(options)) return [3 /*break*/, 5];
                    return [4 /*yield*/, tryGetServerGeneratedConfig(fetchDataPromisesObject, options)];
                case 4:
                    res = _b.sent();
                    serverConfig = res.data;
                    telemetryParams.Completed = res.success;
                    _b.label = 5;
                case 5:
                    if (!canGetClientGeneratedConfig(options)) return [3 /*break*/, 7];
                    getClientGeneratedConfigEnabled = true;
                    // If configured for migration flow, check if client config should be generated
                    if (isMigrationFlow(options)) {
                        getClientGeneratedConfigEnabled =
                            options.isGetClientGeneratedConfigEnabled(featureGates.result);
                    }
                    if (!getClientGeneratedConfigEnabled) return [3 /*break*/, 7];
                    getClientGeneratedConfig = options.getClientGeneratedConfig;
                    return [4 /*yield*/, tryGetClientGeneratedConfig(fetchDataPromisesObject, {
                            key: key,
                            getClientGeneratedConfig: getClientGeneratedConfig,
                        })];
                case 6:
                    res = _b.sent();
                    clientConfig = res.data;
                    telemetryParams.Completed =
                        telemetryParams.Completed || res.success;
                    _b.label = 7;
                case 7:
                    if (telemetryParams.Completed) {
                        if (serverConfig !== undefined && clientConfig !== undefined) {
                            configsAreEqual = validateConfigEquality(serverConfig, clientConfig, key);
                            if (configsAreEqual) {
                                telemetryParams.Status = 'Client';
                                result = clientConfig;
                            }
                            else {
                                telemetryParams.Status = 'Mismatch';
                                result = serverConfig;
                            }
                        }
                        else if (clientConfig !== undefined) {
                            telemetryParams.Status = 'Client';
                            result = clientConfig;
                        }
                        else if (serverConfig !== undefined) {
                            telemetryParams.Status = 'Server';
                            result = serverConfig;
                        }
                    }
                    mark("Fetch_".concat(key, "_").concat(telemetryParams.Status));
                    logFetchConfigInternalFinished(telemetryParams);
                    return [2 /*return*/, result];
            }
        });
    });
}
/**
 * Attempts to remove functionality to generate server config if client config can be generated instead.
 * This is done by removing getServerGeneratedConfig callback function from options object.
 * Requires ClientConfigStandalone.Enabled flight to be enabled
 */
function tryDisableGetServerConfig(featureGates, options) {
    var _a, _b;
    if (((_b = (_a = featureGates.result) === null || _a === void 0 ? void 0 : _a.clientConfigStandaloneGates) === null || _b === void 0 ? void 0 : _b.clientConfigStandaloneEnabled) &&
        canGetClientGeneratedConfig(options)) {
        options = {
            key: options.key,
            getClientGeneratedConfig: options.getClientGeneratedConfig,
        };
    }
    return options;
}
/**
 * Function which attempts to generate config object using API data via getClientGeneratedConfig callback function.
 */
function tryGetClientGeneratedConfig(fetchDataPromisesObject, options) {
    return __awaiter(this, void 0, void 0, function () {
        var getClientGeneratedConfig, key, featureGatesResult, featureGates, _a, featureSettingsResult, featureSettings, _b, userEntitlementsResult, userEntitlements, _c, componentContext, identity, clientConfig, err_1, eventName, message;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    getClientGeneratedConfig = options.getClientGeneratedConfig, key = options.key;
                    _d.label = 1;
                case 1:
                    _d.trys.push([1, 13, , 14]);
                    featureGatesResult = getFeatureGates();
                    if (!(featureGatesResult === null || featureGatesResult === void 0 ? void 0 : featureGatesResult.result)) return [3 /*break*/, 2];
                    _a = featureGatesResult;
                    return [3 /*break*/, 4];
                case 2: return [4 /*yield*/, fetchFeatureGates()];
                case 3:
                    _a = _d.sent();
                    _d.label = 4;
                case 4:
                    featureGates = _a;
                    featureSettingsResult = getFeatureSettings();
                    if (!(featureSettingsResult === null || featureSettingsResult === void 0 ? void 0 : featureSettingsResult.result)) return [3 /*break*/, 5];
                    _b = featureSettingsResult;
                    return [3 /*break*/, 7];
                case 5: return [4 /*yield*/, fetchFeatureSettings()];
                case 6:
                    _b = _d.sent();
                    _d.label = 7;
                case 7:
                    featureSettings = _b;
                    userEntitlementsResult = getUserEntitlements();
                    if (!(userEntitlementsResult === null || userEntitlementsResult === void 0 ? void 0 : userEntitlementsResult.result)) return [3 /*break*/, 8];
                    _c = userEntitlementsResult;
                    return [3 /*break*/, 10];
                case 8: return [4 /*yield*/, fetchUserEntitlements()];
                case 9:
                    _c = _d.sent();
                    _d.label = 10;
                case 10:
                    userEntitlements = _c;
                    return [4 /*yield*/, fetchDataPromisesObject.contextPromise];
                case 11:
                    componentContext = _d.sent();
                    return [4 /*yield*/, fetchDataPromisesObject.identityPromise];
                case 12:
                    identity = _d.sent();
                    clientConfig = getClientGeneratedConfig({
                        featureGates: featureGates.result,
                        featureSettings: featureSettings.result,
                        userEntitlements: userEntitlements.result,
                        componentContext: componentContext,
                        identity: identity,
                    });
                    return [2 /*return*/, { data: clientConfig, success: true }];
                case 13:
                    err_1 = _d.sent();
                    eventName = "".concat(key, "_ClientGenerated_Error");
                    message = "Fetching client generated ".concat(key, ", but encountered an error: ").concat(err_1);
                    logError({
                        eventName: eventName,
                        errorType: 'FetchConfigInternal_Error',
                        message: message,
                    });
                    return [3 /*break*/, 14];
                case 14: return [2 /*return*/, { data: undefined, success: false }];
            }
        });
    });
}
/**
 * Function which attempts to obtain config object using getServerGeneratedConfig callback function.
 */
function tryGetServerGeneratedConfig(fetchDataPromisesObject, options) {
    return __awaiter(this, void 0, void 0, function () {
        var getServerGeneratedConfig, key, context, serverConfig, err_2, eventName, message;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    getServerGeneratedConfig = options.getServerGeneratedConfig, key = options.key;
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 4, , 5]);
                    return [4 /*yield*/, fetchDataPromisesObject.contextPromise];
                case 2:
                    context = _a.sent();
                    return [4 /*yield*/, getServerGeneratedConfig(context)];
                case 3:
                    serverConfig = _a.sent();
                    return [2 /*return*/, { data: serverConfig, success: true }];
                case 4:
                    err_2 = _a.sent();
                    eventName = "".concat(key, "_ServerGenerated_Error");
                    message = "Fetching server generated ".concat(key, ", but encountered an error: ").concat(err_2);
                    logError({
                        eventName: eventName,
                        errorType: 'FetchConfigInternal_Error',
                        message: message,
                    });
                    return [3 /*break*/, 5];
                case 5: return [2 /*return*/, { data: undefined, success: false }];
            }
        });
    });
}
// Type Guard Helper Functions
/**
 * Indicates that options contain all necessary properties for migration flow
 */
function isMigrationFlow(options) {
    var _a = options, isGetClientGeneratedConfigEnabled = _a.isGetClientGeneratedConfigEnabled, getServerGeneratedConfig = _a.getServerGeneratedConfig, getClientGeneratedConfig = _a.getClientGeneratedConfig;
    return (isGetClientGeneratedConfigEnabled !== undefined &&
        getServerGeneratedConfig !== undefined &&
        getClientGeneratedConfig !== undefined);
}
/**
 * Indicates that options contain all necessary properties for client generated flow
 */
function canGetClientGeneratedConfig(options) {
    var getClientGeneratedConfig = options.getClientGeneratedConfig;
    return getClientGeneratedConfig !== undefined;
}
/**
 * Indicates that options contain all necessary properties for server generated flow
 */
function canGetServerGeneratedConfig(options) {
    var getServerGeneratedConfig = options.getServerGeneratedConfig;
    return getServerGeneratedConfig !== undefined;
}
//# sourceMappingURL=fetchConfigInternal.js.map